import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './styles/navBar.css';
import ThemeToggle from './ThemeToggle';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check if dark mode is enabled in localStorage or on the document element
    return localStorage.theme === 'dark' || 
           (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches) ||
           document.documentElement.classList.contains('dark');
  });
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Monitor theme changes
  useEffect(() => {
    // Update the dark mode state immediately
    setIsDarkMode(document.documentElement.classList.contains('dark'));
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDarkMode(document.documentElement.classList.contains('dark'));
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    // Listen to localStorage changes (for cross-tab sync)
    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        setIsDarkMode(e.newValue === 'dark' || document.documentElement.classList.contains('dark'));
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  return (
    <>
      <header 
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          isScrolled 
          ? 'bg-white/95 dark:bg-gray-900/95 shadow-lg backdrop-blur-md py-2' 
          : 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm py-4'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center group">
              <div className="relative overflow-hidden">
                {/* Dark mode logo */}
                <img 
                  src="/logo.png" 
                  alt="Logo for dark mode" 
                  className={`h-10 sm:h-12 w-auto transition-transform duration-300 group-hover:scale-110 ${isDarkMode ? 'block' : 'hidden'}`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML += '<span class="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#8f00ff] to-[#ffd700] bg-clip-text text-transparent">OLAHUB</span>';
                  }}
                />
                
                {/* Light mode logo */}
                <img 
                  src="/logodark.png" 
                  alt="Logo for light mode" 
                  className={`h-10 sm:h-12 w-auto transition-transform duration-300 group-hover:scale-110 ${isDarkMode ? 'hidden' : 'block'}`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML += '<span class="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#8f00ff] to-[#ffd700] bg-clip-text text-transparent">OLAHUB</span>';
                  }}
                />
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <NavLink to="/" active={location.pathname === '/'}>Home</NavLink>
              <NavLink to="/projects" active={location.pathname === '/projects'}>Projects</NavLink>
              <NavLink to="/about" active={location.pathname === '/about'}>About</NavLink>
              <NavLink to="/blog" active={location.pathname === '/blog'}>Blog</NavLink>
              <NavLink to="/contact" active={location.pathname === '/contact'}>Contact</NavLink>
            </nav>

            <div className="hidden md:flex items-center ml-6">
              <ThemeToggle />
            </div>

            <div className="flex items-center md:hidden">
              <div className="mr-3">
                <ThemeToggle />
              </div>
              <button 
                className="flex items-center p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#8f00ff] dark:focus:ring-[#ffd700] z-50"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-expanded={isMenuOpen}
              >
                <span className="sr-only">{isMenuOpen ? 'Close menu' : 'Open menu'}</span>
                <svg 
                  className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
                <svg 
                  className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Full screen mobile menu overlay */}
      <div 
        className={`fixed inset-0 bg-white/70 dark:bg-gray-900/70 backdrop-blur-lg z-40 md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen 
          ? 'opacity-100 pointer-events-auto' 
          : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col h-full justify-center items-center p-4">
          <div className="w-full max-w-sm space-y-4">
            <MobileNavLink to="/" active={location.pathname === '/'}>Home</MobileNavLink>
            <MobileNavLink to="/projects" active={location.pathname === '/projects'}>Projects</MobileNavLink>
            <MobileNavLink to="/about" active={location.pathname === '/about'}>About</MobileNavLink>
            <MobileNavLink to="/blog" active={location.pathname === '/blog'}>Blog</MobileNavLink>
            <MobileNavLink to="/contact" active={location.pathname === '/contact'}>Contact</MobileNavLink>
          </div>
        </div>
      </div>
    </>
  );
};

const NavLink = ({ to, active, children }) => (
  <Link 
    to={to} 
    className="relative group"
  >
    <span className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
      active 
      ? 'text-[#8f00ff] dark:text-[#ffd700]' 
      : 'text-gray-700 dark:text-gray-300 group-hover:text-[#8f00ff] dark:group-hover:text-[#ffd700]'
    }`}>
      {children}
    </span>
    <span className={`absolute bottom-0 left-0 w-full h-0.5 transform origin-left transition-transform duration-300 ${
      active 
      ? 'bg-[#8f00ff] dark:bg-[#ffd700] scale-x-100' 
      : 'bg-[#8f00ff] dark:bg-[#ffd700] scale-x-0 group-hover:scale-x-100'
    }`}></span>
  </Link>
);

const MobileNavLink = ({ to, active, children }) => (
  <Link 
    to={to} 
    className={`block py-4 text-center text-xl font-medium transition-all duration-300 ${
      active 
      ? 'text-[#8f00ff] dark:text-[#ffd700] scale-110' 
      : 'text-gray-800 dark:text-gray-200 hover:text-[#8f00ff] dark:hover:text-[#ffd700] hover:scale-110'
    }`}
  >
    {children}
  </Link>
);

export default Navbar;